import { defineMessage, defineMessages, MessageDescriptor } from 'react-intl'

export enum AuroraCareOption {
  SELF_TRIAGE = 'selfTriage',
  CHILD_TRIAGE = 'childTriage',
  MENTAL_HEALTH_COACHING = 'mentalHealthCoaching',
  PARENTING_COACHING = 'parentingCoaching',
  COUPLES_THERAPY = 'couplesTherapy',
  FAMILY_THERAPY = 'familyTherapy',
  CONTROLLED_MEDS = 'controlledMeds',
  MEDICATION_CONSULTATION = 'medicationConsultation',
  MEDICATION_CONSULTATION_CHILDREN = 'medicationConsultationChildren',
  MEDICATION_CONSULTATION_TEENS = 'medicationConsultationTeens',
  AUD_SUD = 'audSud', // alcohol use disorder or substance use disorder
  COMPLEX_CARE = 'complexCare',
  DBT = 'dbt', // support for self harm
  DBT_INTAKE = 'dbtIntake', // intake form
  IOP = 'iop', // intensive outpatient program
  PHP = 'php', // partial hospitalization program
  RESIDENTIAL_TREATMENT = 'residentialTreatment',
  INPATIENT_HOSPITALIZATION = 'inpatientHospitalization',
  ONSITE_THERAPY = 'onsiteTherapy',
}

export const AURORA_GRID_CARE_OPTIONS = [
  AuroraCareOption.CHILD_TRIAGE,
  AuroraCareOption.MENTAL_HEALTH_COACHING,
  AuroraCareOption.PARENTING_COACHING,
  AuroraCareOption.COUPLES_THERAPY,
  AuroraCareOption.FAMILY_THERAPY,
  AuroraCareOption.MEDICATION_CONSULTATION,
] as const

export const isGridSupported = (option: AuroraCareOption) => {
  return AURORA_GRID_CARE_OPTIONS.includes(option as any)
}

export interface AuroraCareOptionCardConfig {
  id: AuroraCareOption
  title: MessageDescriptor
  description?: MessageDescriptor
  imageKey: string
  onCardPress: () => void
  buttonText?: MessageDescriptor
  // isHero and isSecondary are grid positions; not finalized yet
  isHero?: boolean
  isSecondary?: boolean
}

// used for card titles and modal headers (i.e. AuroraCareOptionCard and ProgramDetailsModal)
export const AURORA_CARE_OPTIONS_HEADERS = defineMessages({
  [AuroraCareOption.SELF_TRIAGE]: {
    defaultMessage: 'Personalized care plan',
    description: 'Header of content for self triage care option',
  },
  [AuroraCareOption.CHILD_TRIAGE]: {
    defaultMessage: 'Care for a child or teen',
    description: 'Header of content for child triage care option',
  },
  [AuroraCareOption.MENTAL_HEALTH_COACHING]: {
    defaultMessage: 'Mental health coaching',
    description: 'Header of content for mental health coaching care option',
  },
  [AuroraCareOption.PARENTING_COACHING]: {
    defaultMessage: 'Coaching for parents',
    description: 'Header of content for parenting coaching care option',
  },
  [AuroraCareOption.COUPLES_THERAPY]: {
    defaultMessage: 'Couples therapy',
    description: 'Header of content for couples therapy care option',
  },
  [AuroraCareOption.FAMILY_THERAPY]: {
    defaultMessage: 'Family therapy',
    description: 'Header of content for family therapy care option',
  },
  [AuroraCareOption.CONTROLLED_MEDS]: {
    defaultMessage: 'Controlled medication',
    description: 'Header of content for controlled medication care option',
  },
  [AuroraCareOption.MEDICATION_CONSULTATION]: {
    defaultMessage: 'Medication consultation',
    description: 'Header of content for medication consultation care option',
  },
  [AuroraCareOption.MEDICATION_CONSULTATION_CHILDREN]: {
    defaultMessage: 'Medication consultation for children',
    description: 'Header of content for medication consultation for children care option',
  },
  [AuroraCareOption.MEDICATION_CONSULTATION_TEENS]: {
    defaultMessage: 'Medication consultation for teens',
    description: 'Header of content for medication consultation for teens care option',
  },
  [AuroraCareOption.AUD_SUD]: {
    defaultMessage: 'Treatment for alcohol or substance use',
    description: 'Header of content for alcohol or substance use treatment care option',
  },
  [AuroraCareOption.COMPLEX_CARE]: {
    defaultMessage: 'Complex care',
    description: 'Header of content for complex care option',
  },
  [AuroraCareOption.DBT]: {
    defaultMessage: 'Support for self-harm',
    description: 'Header of content for self-harm support care option',
  },
  [AuroraCareOption.DBT_INTAKE]: {
    defaultMessage: 'The next step',
    description: 'Header of content for DBT intake form care option',
  },
  [AuroraCareOption.IOP]: {
    defaultMessage: 'Intensive outpatient program',
    description: 'Header of content for intensive outpatient program care option',
  },
  [AuroraCareOption.PHP]: {
    defaultMessage: 'Partial hospitalization program',
    description: 'Header of content for partial hospitalization program care option',
  },
  [AuroraCareOption.RESIDENTIAL_TREATMENT]: {
    defaultMessage: 'Residential treatment',
    description: 'Header of content for residential treatment care option',
  },
  [AuroraCareOption.INPATIENT_HOSPITALIZATION]: {
    defaultMessage: 'Inpatient hospitalization',
    description: 'Header of content for inpatient hospitalization care option',
  },
  [AuroraCareOption.ONSITE_THERAPY]: {
    defaultMessage: 'Therapy at {company}',
    description: 'Header of content for onsite therapy care option',
  },
})

export const AuroraCareOptionsMetadata: { [key in AuroraCareOption]: AuroraCareOptionCardConfig } = {
  [AuroraCareOption.SELF_TRIAGE]: {
    id: AuroraCareOption.SELF_TRIAGE,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.SELF_TRIAGE],
    description: defineMessage({
      defaultMessage: 'Let’s guide you to the right support for your needs',
      description: 'Self triage option - description',
    }),
    buttonText: defineMessage({
      defaultMessage: 'Find care for myself',
      description: 'Self triage option - button text',
    }),
    imageKey: 'auroraCareOptionSelfTriage',
    isHero: true,
    onCardPress: () => {},
  },
  [AuroraCareOption.CHILD_TRIAGE]: {
    id: AuroraCareOption.CHILD_TRIAGE,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.CHILD_TRIAGE],
    description: defineMessage({
      defaultMessage: 'Search for care based on your child’s needs',
      description: 'Child triage option - description',
    }),
    buttonText: defineMessage({
      defaultMessage: 'Find care for my child',
      description: 'Child triage option - button text',
    }),
    imageKey: 'auroraCareOptionChildTriage',
    isSecondary: true,
    onCardPress: () => {},
  },
  [AuroraCareOption.MENTAL_HEALTH_COACHING]: {
    id: AuroraCareOption.MENTAL_HEALTH_COACHING,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.MENTAL_HEALTH_COACHING],
    description: defineMessage({
      defaultMessage: 'Meet with a coach to improve your wellbeing',
      description: 'Mental health coaching option - description',
    }),
    buttonText: defineMessage({
      defaultMessage: 'Find a coach',
      description: 'Mental health coaching option - button text',
    }),
    imageKey: 'auroraCareOptionMentalHealthCoaching',
    isSecondary: true,
    onCardPress: () => {},
  },
  [AuroraCareOption.PARENTING_COACHING]: {
    id: AuroraCareOption.PARENTING_COACHING,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.PARENTING_COACHING],
    description: defineMessage({
      defaultMessage: 'Get support to handle parenting challenges',
      description: 'Parenting coaching option - description',
    }),
    imageKey: 'auroraCareOptionParentingCoaching',
    onCardPress: () => {},
  },
  [AuroraCareOption.COUPLES_THERAPY]: {
    id: AuroraCareOption.COUPLES_THERAPY,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.COUPLES_THERAPY],
    description: defineMessage({
      defaultMessage: 'Navigate challenges in your relationship',
      description: 'Couples therapy option - description',
    }),
    imageKey: 'auroraCareOptionCouplesTherapy',
    onCardPress: () => {},
  },
  [AuroraCareOption.FAMILY_THERAPY]: {
    id: AuroraCareOption.FAMILY_THERAPY,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.FAMILY_THERAPY],
    description: defineMessage({
      defaultMessage: 'Bring your family together to overcome challenges',
      description: 'Family therapy option - description',
    }),
    imageKey: 'auroraCareOptionFamilyTherapy',
    onCardPress: () => {},
  },
  [AuroraCareOption.CONTROLLED_MEDS]: {
    id: AuroraCareOption.CONTROLLED_MEDS,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.CONTROLLED_MEDS],
    description: defineMessage({
      defaultMessage: 'Consult a prescriber for a prescription refill',
      description: 'Controlled medication option - description',
    }),
    imageKey: 'auroraCareOptionControlledMeds',
    onCardPress: () => {},
  },
  [AuroraCareOption.MEDICATION_CONSULTATION]: {
    id: AuroraCareOption.MEDICATION_CONSULTATION,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.MEDICATION_CONSULTATION],
    description: defineMessage({
      defaultMessage: 'Work with a prescriber to consider medication',
      description: 'Medication consultation option - description',
    }),
    imageKey: 'auroraCareOptionMedicationConsultation',
    onCardPress: () => {},
  },
  [AuroraCareOption.MEDICATION_CONSULTATION_CHILDREN]: {
    id: AuroraCareOption.MEDICATION_CONSULTATION_CHILDREN,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.MEDICATION_CONSULTATION_CHILDREN],
    description: defineMessage({
      defaultMessage: 'Ask about medication for your child',
      description: 'Medication consultation for children option - description',
    }),
    imageKey: 'auroraCareOptionMedicationConsultationChildrenTeens',
    onCardPress: () => {},
  },
  [AuroraCareOption.MEDICATION_CONSULTATION_TEENS]: {
    id: AuroraCareOption.MEDICATION_CONSULTATION_TEENS,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.MEDICATION_CONSULTATION_TEENS],
    description: defineMessage({
      defaultMessage: 'Ask about medication for your teen',
      description: 'Medication consultation for teens option - description',
    }),
    imageKey: 'auroraCareOptionMedicationConsultationChildrenTeens',
    onCardPress: () => {},
  },
  [AuroraCareOption.AUD_SUD]: {
    id: AuroraCareOption.AUD_SUD,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.AUD_SUD],
    description: defineMessage({
      defaultMessage: 'Get tailored support for substance use recovery',
      description: 'Alcohol or substance use treatment option - description',
    }),
    imageKey: 'auroraCareOptionAlcoholUseDisorder',
    onCardPress: () => {},
  },
  [AuroraCareOption.COMPLEX_CARE]: {
    id: AuroraCareOption.COMPLEX_CARE,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.COMPLEX_CARE],
    description: defineMessage({
      defaultMessage: 'Access intensive and specialized programs',
      description: 'Complex care option - description',
    }),
    imageKey: 'auroraCareOptionComplexCare',
    onCardPress: () => {},
  },
  [AuroraCareOption.DBT]: {
    id: AuroraCareOption.DBT,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.DBT],
    description: defineMessage({
      defaultMessage: 'Get help coping with difficult emotions',
      description: 'Self-harm support option - description',
    }),
    imageKey: 'auroraCareOptionDBT',
    onCardPress: () => {},
  },
  [AuroraCareOption.DBT_INTAKE]: {
    id: AuroraCareOption.DBT_INTAKE,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.DBT_INTAKE],
    description: defineMessage({
      defaultMessage: 'Complete the intake form for Dialectical Behavior Therapy (DBT)',
      description: 'DBT intake form - description',
    }),
    buttonText: defineMessage({
      defaultMessage: 'Begin',
      description: 'DBT intake form - button text',
    }),
    imageKey: 'auroraCareOptionSelfTriage',
    isHero: true,
    onCardPress: () => {},
  },
  [AuroraCareOption.IOP]: {
    id: AuroraCareOption.IOP,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.IOP],
    description: defineMessage({
      defaultMessage: 'Get frequent, intensive care virtually or at a clinic',
      description: 'Intensive outpatient program option - description',
    }),
    imageKey: 'auroraCareOptionIOP',
    onCardPress: () => {},
  },
  [AuroraCareOption.PHP]: {
    id: AuroraCareOption.PHP,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.PHP],
    description: defineMessage({
      defaultMessage: 'Live at home and get daily intensive support',
      description: 'Partial hospitalization program option - description',
    }),
    imageKey: 'auroraCareOptionPHP',
    onCardPress: () => {},
  },
  [AuroraCareOption.RESIDENTIAL_TREATMENT]: {
    id: AuroraCareOption.RESIDENTIAL_TREATMENT,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.RESIDENTIAL_TREATMENT],
    description: defineMessage({
      defaultMessage: '24/7 in-facility care to build sustainable recovery',
      description: 'Residential treatment option - description',
    }),
    imageKey: 'auroraCareOptionResidentialTreatment',
    onCardPress: () => {},
  },
  [AuroraCareOption.INPATIENT_HOSPITALIZATION]: {
    id: AuroraCareOption.INPATIENT_HOSPITALIZATION,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.INPATIENT_HOSPITALIZATION],
    description: defineMessage({
      defaultMessage: '24/7 care for an urgent mental health crisis',
      description: 'Inpatient hospitalization option - description',
    }),
    imageKey: 'auroraCareOptionInpatientHospitalization',
    onCardPress: () => {},
  },
  [AuroraCareOption.ONSITE_THERAPY]: {
    id: AuroraCareOption.ONSITE_THERAPY,
    title: AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.ONSITE_THERAPY],
    description: defineMessage({
      defaultMessage: 'Meet with an onsite therapist at {company}',
      description: 'Onsite therapy option - description',
    }),
    imageKey: 'auroraCareOptionOnsiteTherapy',
    onCardPress: () => {},
  },
}
